import { useCallback, useEffect, useRef } from "react";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { ConfirmAlert } from "../../../components/Alerts/Alerts";
import { getNameBusinessTheme } from "../../../actions/getters";
import {
  startCreateUpdatePaymentMethodLinkAds,
  startGetPayInvoiceUrl,
} from "../../../actions/payments";
import { setShowBackdrop } from "../../../actions/ui";
import {
  BILLING_PENDING_SUBSCRIPTION_PAYMENT_DESCRIPTION,
  BUTTON_CHANGE_PAYMENT_METHODS,
  DATE_SHORT,
  MODAL_PAY_SUBSCRIPTION_PAY_NOW,
  MODAL_PAY_SUBSCRIPTION_PENDING_PAYMENT_TITLE,
} from "../../../i18n/keysTranslations";
import { ALERT_ICON_TYPE_ERROR } from "../../../utils/constants";
import { getPaymentFailedLimitDate } from "../../../utils/pendingPackagePlanInvoice";

const getAlertIdentity = (userID, invoiceKey) => {
  if (!userID || !invoiceKey) return null;
  return `${userID}:${invoiceKey}`;
};

const redirectToStripeUrl = (response) => {
  const url = response?.ok ? response?.data?.url : null;
  if (!url) {
    return false;
  }
  window.location.href = url;
  return true;
};

export const usePendingSubscriptionPaymentAlert = ({
  invoice = null,
  invoiceKey = null,
  enabled = true,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const userID = useSelectorApp((state) => state.auth.user?.uid);
  const paymentFailedLimitDate = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo?.paymentFailedLimitDate
  );
  const shownAlertIdentityRef = useRef(null);

  const payPendingInvoice = useCallback(async () => {
    if (!invoiceKey) return;
    const paymentIntent = invoice?.paymentID ?? invoiceKey;
    const result = await dispatch(
      startGetPayInvoiceUrl({ paymentIntent, coldBoot: false })
    );
    return redirectToStripeUrl(result);
  }, [dispatch, invoice?.paymentID, invoiceKey]);

  const openPaymentMethodsPortal = useCallback(async () => {
    const response = await dispatch(startCreateUpdatePaymentMethodLinkAds());
    return redirectToStripeUrl(response);
  }, [dispatch]);

  useEffect(() => {
    const alertIdentity = getAlertIdentity(userID, invoiceKey);
    const canShowAlert = Boolean(enabled && invoice && alertIdentity);

    if (!canShowAlert || shownAlertIdentityRef.current === alertIdentity) {
      return undefined;
    }

    shownAlertIdentityRef.current = alertIdentity;
    let isMounted = true;

    const NAME_BUSINESS = dispatch(getNameBusinessTheme());
    const limitDateRaw = getPaymentFailedLimitDate({
      paymentFailedLimitDate,
      dueDate: invoice?.dueDate,
    });
    const limitDateFormatted =
      limitDateRaw != null
        ? t(DATE_SHORT, {
            date: new Date(limitDateRaw),
          })
        : "";

    const showPendingPaymentAlert = async () => {
      const result = await ConfirmAlert({
        title: t(MODAL_PAY_SUBSCRIPTION_PENDING_PAYMENT_TITLE, {
          value: invoice?.value,
          currency: (invoice?.currency || "USD").toUpperCase(),
        }),
        text: t(BILLING_PENDING_SUBSCRIPTION_PAYMENT_DESCRIPTION, {
          limitDate: limitDateFormatted,
          NAME_BUSINESS,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
        confirmButtonText: t(MODAL_PAY_SUBSCRIPTION_PAY_NOW),
        cancelButtonText: t(BUTTON_CHANGE_PAYMENT_METHODS),
      });

      if (!isMounted) {
        return;
      }

      if (!result.isConfirmed && result.dismiss !== "cancel") {
        return;
      }

      dispatch(setShowBackdrop(true));
      let redirected = false;
      try {
        redirected = result.isConfirmed
          ? await payPendingInvoice()
          : await openPaymentMethodsPortal();
      } finally {
        if (isMounted && !redirected) {
          dispatch(setShowBackdrop(false));
        }
      }
    };

    showPendingPaymentAlert();

    return () => {
      isMounted = false;
    };
  }, [
    enabled,
    invoice,
    invoiceKey,
    userID,
    paymentFailedLimitDate,
    dispatch,
    t,
    payPendingInvoice,
    openPaymentMethodsPortal,
  ]);
};
